import { ExpandMore, Sort } from '@tunasong/icons'
import type { SortOptions } from '@tunasong/models'
import cn from 'classnames'
import type { FC } from 'react'
import {
  Menu,
  MenuContent,
  MenuGroupLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '../primitives/index.js'
import {
  describeSort,
  getOrderOptions,
  isSortField,
  selectSortField,
  sortFieldLabels,
  sortFields,
} from './sort-filter-util.js'
import styles from './sort-menu.module.css'

export interface SortMenuProps {
  className?: string
  value: SortOptions
  /**
   * The list is narrowed by text, so dragging to reorder is off (GH #2519 A): a partial list's order is
   * ambiguous, and it changes on every key. The menu says why. @default false
   */
  reorderBlocked?: boolean
  onChange(options: SortOptions): void
}

/** A compact trigger showing the current sort, opening a menu to pick the field and direction */
export const SortMenu: FC<SortMenuProps> = props => {
  const { className, value, reorderBlocked = false, onChange } = props

  const { field, label, direction, accessibleName } = describeSort(value)

  const handleFieldChange = (next: unknown) => {
    if (isSortField(next)) {
      onChange(selectSortField(value, next))
    }
  }

  const handleOrderChange = (next: unknown) => {
    if (next === 'asc' || next === 'desc') {
      onChange({ ...value, order: next })
    }
  }

  return (
    <Menu>
      <MenuTrigger className={cn(styles.trigger, className)} aria-label={accessibleName}>
        <Sort size={16} aria-hidden="true" />
        <span>{label}</span>
        {direction ? <span className={styles.direction}>· {direction}</span> : null}
        <ExpandMore size={14} aria-hidden="true" />
      </MenuTrigger>
      <MenuContent className={styles.menu}>
        <MenuRadioGroup value={field} onValueChange={handleFieldChange}>
          <MenuGroupLabel>Sort by</MenuGroupLabel>
          {sortFields.map(f => (
            <MenuRadioItem key={f} value={f}>
              {sortFieldLabels[f]}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
        <MenuSeparator />
        {field === 'order' ? (
          <p className={styles.hint}>
            {reorderBlocked ? 'Clear the filter to reorder.' : 'Drag items in the list to arrange them.'}
          </p>
        ) : (
          <MenuRadioGroup value={value.order ?? 'desc'} onValueChange={handleOrderChange}>
            <MenuGroupLabel>Order</MenuGroupLabel>
            {getOrderOptions(field).map(o => (
              <MenuRadioItem key={o.value} value={o.value}>
                {o.label}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        )}
      </MenuContent>
    </Menu>
  )
}

export default SortMenu
